import React, { useRef, useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import SimplePeer from "simple-peer";
import io from "socket.io-client";
import { v4 as uuidv4 } from "uuid";
import { Buffer } from "buffer";
import process from "process";
import { useTheme } from "../ThemeContext";
import "../meet.css";

const socket = io("https://meet-blazync-backend.onrender.com");

const Meet = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();

  const [userId] = useState(() => uuidv4().substring(0, 8));
  const [, setStream]                     = useState(null);
  const [peers, setPeers]                 = useState({});   // { peerId: { peer, stream } }
  const [incomingCall, setIncomingCall]   = useState(null);
  const [isMuted, setIsMuted]             = useState(false);
  const [isCameraOn, setIsCameraOn]       = useState(true);
  const [facingMode, setFacingMode]       = useState("user");
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [sidebarTab, setSidebarTab]       = useState("people"); // 'people' | 'info'
  const [meetingType, setMeetingType]     = useState("group");  // '1:1' | 'group'
  const [callTarget, setCallTarget]       = useState("");
  const [linkCopied, setLinkCopied]       = useState(false);
  const [idCopied, setIdCopied]           = useState(false);
  const [videoStarted, setVideoStarted]   = useState(false);
  const [elapsed, setElapsed]             = useState(0);

  const myVideoRef      = useRef();
  const peerRefs        = useRef({});   // { peerId: videoElement }
  const streamRef       = useRef(null); // always up-to-date camera stream
  const screenStreamRef = useRef(null); // screen share stream

  const meetingLink = `${window.location.origin}/meet/${roomId}`;

  // ── Elapsed timer ──────────────────────────────────────────
  useEffect(() => {
    if (!videoStarted) return;
    const t = setInterval(() => setElapsed(s => s + 1), 1000);
    return () => clearInterval(t);
  }, [videoStarted]);

  const formatTime = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return h
      ? `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`
      : `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  };

  // ── Init socket + polyfills ─────────────────────────────────
  useEffect(() => {
    window.Buffer  = Buffer;
    window.process = process;
    socket.emit("registerUser", userId);
  }, [userId]);

  // ── Listen for incoming calls (always) ─────────────────────
  useEffect(() => {
    const handler = ({ signal, from }) => setIncomingCall({ from, signal });
    socket.on("callUser", handler);
    return () => socket.off("callUser", handler);
  }, []);

  // ── Start Video ─────────────────────────────────────────────
  const startVideo = useCallback(async (mode = facingMode) => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: mode },
        audio: true,
      });
      setStream(s);
      streamRef.current = s;
      if (myVideoRef.current) myVideoRef.current.srcObject = s;
      setVideoStarted(true);
      return s;
    } catch {
      alert("Could not access camera/microphone. Please allow permissions.");
      return null;
    }
  }, [facingMode]);

  // ── Toggle mute ─────────────────────────────────────────────
  const toggleMute = () => {
    if (streamRef.current) {
      streamRef.current.getAudioTracks().forEach(t => (t.enabled = !t.enabled));
      setIsMuted(m => !m);
    }
  };

  // ── Toggle camera ───────────────────────────────────────────
  const toggleCamera = () => {
    if (streamRef.current) {
      streamRef.current.getVideoTracks().forEach(t => (t.enabled = !t.enabled));
      setIsCameraOn(c => !c);
    }
  };

  // ── Switch camera (front ↔ back) ─────────────────────────────
  const switchCamera = async () => {
    const newMode = facingMode === "user" ? "environment" : "user";
    setFacingMode(newMode);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      await startVideo(newMode);
    }
  };

  // ── Screen share helpers ─────────────────────────────────────
  const replaceVideoTrackInPeers = (newTrack) => {
    setPeers(current => {
      Object.values(current).forEach(({ peer }) => {
        if (peer && !peer.destroyed) {
          const sender = peer._pc?.getSenders().find(s => s.track?.kind === "video");
          if (sender) sender.replaceTrack(newTrack).catch(() => {});
        }
      });
      return current;
    });
  };

  const stopScreenShare = useCallback(() => {
    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach(t => t.stop());
      screenStreamRef.current = null;
    }
    setIsScreenSharing(false);
    // Revert local preview to camera
    if (streamRef.current && myVideoRef.current) {
      myVideoRef.current.srcObject = streamRef.current;
    }
    // Put camera track back in all peer connections
    const camTrack = streamRef.current?.getVideoTracks()[0];
    if (camTrack) replaceVideoTrackInPeers(camTrack);
  }, []); // eslint-disable-line

  const startScreenShare = useCallback(async () => {
    try {
      const screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: { cursor: "always" },
        audio: false,
      });
      screenStreamRef.current = screenStream;
      setIsScreenSharing(true);

      if (myVideoRef.current) myVideoRef.current.srcObject = screenStream;

      const screenTrack = screenStream.getVideoTracks()[0];
      replaceVideoTrackInPeers(screenTrack);

      // Auto-revert when user clicks browser's "Stop sharing" button
      screenTrack.addEventListener("ended", stopScreenShare, { once: true });
    } catch (err) {
      if (err.name !== "NotAllowedError") console.error("Screen share error:", err);
    }
  }, [stopScreenShare]); // eslint-disable-line

  const toggleScreenShare = () => {
    if (!videoStarted) return alert("Start your camera first.");
    isScreenSharing ? stopScreenShare() : startScreenShare();
  };

  // ── Create a peer connection ─────────────────────────────────
  const createPeer = useCallback((targetId, initiator, currentStream) => {
    const p = new SimplePeer({ initiator, trickle: false, stream: currentStream });

    p.on("signal", data => {
      if (initiator) {
        socket.emit("callUser", { signal: data, to: targetId });
      } else {
        socket.emit("callAccepted", { signal: data, to: targetId });
      }
    });

    p.on("stream", remoteStream => {
      setPeers(prev => ({ ...prev, [targetId]: { peer: p, stream: remoteStream } }));
      // Attach to video element if it already exists
      if (peerRefs.current[targetId]) {
        peerRefs.current[targetId].srcObject = remoteStream;
      }
    });

    p.on("close",  () => removePeer(targetId));
    p.on("error",  () => removePeer(targetId));

    return p;
  }, []); // eslint-disable-line

  const removePeer = (id) => {
    setPeers(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  // ── Call a peer ──────────────────────────────────────────────
  const callPeer = () => {
    const target = callTarget.trim();
    if (!target)          return alert("Enter a peer ID to call.");
    if (!streamRef.current) return alert("Start your video first.");
    if (peers[target])    return alert("Already connected to this peer.");

    const p = createPeer(target, true, streamRef.current);
    setPeers(prev => ({ ...prev, [target]: { peer: p, stream: null } }));

    socket.once("callAccepted", signal => p.signal(signal));
  };

  // ── Accept incoming call ─────────────────────────────────────
  const acceptCall = () => {
    if (!incomingCall) return;
    const currentStream = streamRef.current;
    if (!currentStream) {
      // start video then accept
      startVideo().then(s => {
        if (!s) return;
        const p = createPeer(incomingCall.from, false, s);
        p.signal(incomingCall.signal);
        setPeers(prev => ({ ...prev, [incomingCall.from]: { peer: p, stream: null } }));
        setIncomingCall(null);
      });
      return;
    }
    const p = createPeer(incomingCall.from, false, currentStream);
    p.signal(incomingCall.signal);
    setPeers(prev => ({ ...prev, [incomingCall.from]: { peer: p, stream: null } }));
    setIncomingCall(null);
  };

  // ── Disconnect ───────────────────────────────────────────────
  const disconnectAll = () => {
    // Stop screen share if active
    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach(t => t.stop());
      screenStreamRef.current = null;
      setIsScreenSharing(false);
    }
    Object.values(peers).forEach(({ peer }) => peer?.destroy());
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setStream(null);
    setPeers({});
    setVideoStarted(false);
    setElapsed(0);
    setFacingMode("user"); // reset to front camera for next session
    socket.emit("disconnectCall", userId);
  };

  const handleLeave = () => {
    disconnectAll();
    navigate("/");
  };

  // ── Copy helpers ─────────────────────────────────────────────
  const copyLink = () => {
    navigator.clipboard.writeText(meetingLink);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 1800);
  };

  const copyId = () => {
    navigator.clipboard.writeText(userId);
    setIdCopied(true);
    setTimeout(() => setIdCopied(false), 1800);
  };

  // ── Video tile – assign stream to <video> on mount ───────────
  const PeerVideoTile = ({ peerId, peerStream }) => {
    const ref = useRef();
    useEffect(() => {
      peerRefs.current[peerId] = ref.current;
      if (ref.current && peerStream) ref.current.srcObject = peerStream;
    }, [peerId, peerStream]);
    return (
      <div className="m-video-tile">
        <video ref={ref} autoPlay playsInline />
        <div className="m-tile-label">
          <i className="fas fa-user"></i> {peerId}
        </div>
      </div>
    );
  };

  // ── Build participant list for grid ──────────────────────────
  const peerEntries = Object.entries(peers);
  const totalCount  = 1 + peerEntries.length; // local + peers

  const gridClass = (() => {
    if (totalCount === 1) return "g1";
    if (totalCount === 2) return "g2";
    if (totalCount <= 4)  return "g4";
    if (totalCount <= 6)  return "g6";
    return "g9";
  })();

  return (
    <div className="m-page">
      {/* ── Navbar ── */}
      <nav className="m-nav">
        <div className="m-nav-left">
          <img src="https://aozo.in/sheild_logo.png" alt="AOZO" className="m-nav-logo" />
          <div className="m-room-chip">
            <i className="fas fa-video"></i>
            <span>{roomId}</span>
          </div>
        </div>

        <div className="m-nav-center">
          {videoStarted && (
            <span className="m-timer">
              <i className="fas fa-circle m-live-dot"></i>
              {formatTime(elapsed)}
            </span>
          )}
        </div>

        <div className="m-nav-right">
          <button className="m-icon-btn" onClick={toggleTheme} title="Toggle theme">
            <i className={`fas ${theme === "dark" ? "fa-sun" : "fa-moon"}`}></i>
          </button>
          <button className="m-icon-btn" onClick={copyLink} title="Copy meeting link">
            <i className={`fas ${linkCopied ? "fa-check" : "fa-link"}`}></i>
          </button>
          <button className="m-leave-btn" onClick={handleLeave}>
            <i className="fas fa-sign-out-alt"></i> Leave
          </button>
        </div>
      </nav>

      {/* ── Body = video + sidebar ── */}
      <div className="m-body">
        {/* ── Video section (col-10) ── */}
        <section className="m-video-section">
          {/* Incoming call banner */}
          {incomingCall && (
            <div className="m-incoming-banner">
              <i className="fas fa-phone-alt" style={{ color: "var(--green)" }}></i>
              <span>Incoming call from <strong>{incomingCall.from}</strong></span>
              <button className="m-accept-btn" onClick={acceptCall}>
                <i className="fas fa-phone"></i> Accept
              </button>
              <button className="m-decline-btn" onClick={() => setIncomingCall(null)}>
                <i className="fas fa-phone-slash"></i>
              </button>
            </div>
          )}

          {!videoStarted ? (
            /* ── Pre-join state ── */
            <div className="m-prejoin">
              <div className="m-prejoin-card">
                <i className="fas fa-video m-prejoin-icon"></i>
                <h2>Ready to join?</h2>
                <p>Room: <strong>{roomId}</strong></p>
                <button className="m-start-btn" onClick={() => startVideo()}>
                  <i className="fas fa-video"></i> Start Camera &amp; Join
                </button>
                <p className="m-prejoin-hint">Your camera starts when you click Join</p>
              </div>
            </div>
          ) : (
            <div className={`m-video-grid ${gridClass}`}>
              {/* Local tile */}
              <div className="m-video-tile m-tile-local">
                <video ref={myVideoRef} autoPlay playsInline muted />
                <div className="m-tile-label">
                  {isScreenSharing
                    ? <><i className="fas fa-desktop"></i> You — Screen</>
                    : <><i className="fas fa-user"></i> You ({userId})</>
                  }
                  {isMuted && <i className="fas fa-microphone-slash m-muted-badge"></i>}
                </div>
              </div>

              {/* Peer tiles */}
              {peerEntries.map(([pid, { stream: ps }]) => (
                <PeerVideoTile key={pid} peerId={pid} peerStream={ps} />
              ))}

              {/* Empty placeholders for visual balance when 1 person */}
              {totalCount === 1 && (
                <div className="m-video-tile m-tile-waiting">
                  <div className="m-waiting-inner">
                    <i className="fas fa-user-plus"></i>
                    <p>Invite someone to join</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        {/* ── Sidebar (col-2) ── */}
        <aside className="m-sidebar">
          {/* Tabs */}
          <div className="m-sidebar-tabs">
            <button
              className={`m-tab ${sidebarTab === "people" ? "m-tab-active" : ""}`}
              onClick={() => setSidebarTab("people")}
            >
              <i className="fas fa-users"></i> People
            </button>
            <button
              className={`m-tab ${sidebarTab === "info" ? "m-tab-active" : ""}`}
              onClick={() => setSidebarTab("info")}
            >
              <i className="fas fa-info-circle"></i> Info
            </button>
          </div>

          {/* People tab */}
          {sidebarTab === "people" && (
            <div className="m-sidebar-body">
              {/* Meeting type */}
              <div className="m-section-label">Meeting type</div>
              <div className="m-type-toggle">
                <button
                  className={`m-type-btn ${meetingType === "1:1" ? "m-type-active" : ""}`}
                  onClick={() => setMeetingType("1:1")}
                >
                  <i className="fas fa-user-friends"></i> 1:1
                </button>
                <button
                  className={`m-type-btn ${meetingType === "group" ? "m-type-active" : ""}`}
                  onClick={() => setMeetingType("group")}
                >
                  <i className="fas fa-users"></i> Group
                </button>
              </div>

              {/* Participants */}
              <div className="m-section-label">
                Participants ({1 + peerEntries.length})
              </div>

              <div className="m-participant-list">
                {/* Yourself */}
                <div className="m-participant">
                  <div className="m-participant-avatar">
                    {userId.substring(0, 2).toUpperCase()}
                  </div>
                  <div className="m-participant-info">
                    <span className="m-participant-name">You ({userId})</span>
                    <span className="m-participant-sub">Host</span>
                  </div>
                  <button className="m-participant-copy" onClick={copyId} title="Copy your ID">
                    <i className={`fas ${idCopied ? "fa-check" : "fa-copy"}`}></i>
                  </button>
                </div>

                {/* Connected peers */}
                {peerEntries.map(([pid]) => (
                  <div className="m-participant" key={pid}>
                    <div className="m-participant-avatar m-avatar-peer">
                      {pid.substring(0, 2).toUpperCase()}
                    </div>
                    <div className="m-participant-info">
                      <span className="m-participant-name">{pid}</span>
                      <span className="m-participant-sub m-connected-badge">
                        <i className="fas fa-circle"></i> Connected
                      </span>
                    </div>
                    <button
                      className="m-participant-kick"
                      title="Disconnect"
                      onClick={() => {
                        peers[pid]?.peer?.destroy();
                        removePeer(pid);
                      }}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                ))}
              </div>

              {/* Call input */}
              <div className="m-section-label">Add someone</div>
              <div className="m-call-input-group">
                <input
                  type="text"
                  placeholder="Enter peer ID..."
                  value={callTarget}
                  onChange={e => setCallTarget(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && callPeer()}
                />
                <button
                  className="m-call-btn"
                  onClick={callPeer}
                  disabled={!callTarget.trim() || !videoStarted}
                >
                  <i className="fas fa-phone"></i>
                </button>
              </div>
              {!videoStarted && (
                <p className="m-sidebar-hint">Start your camera first to call</p>
              )}
            </div>
          )}

          {/* Info tab */}
          {sidebarTab === "info" && (
            <div className="m-sidebar-body">
              <div className="m-section-label">Meeting link</div>
              <div className="m-link-box">
                <span className="m-link-text">{meetingLink}</span>
                <button className="m-link-copy-btn" onClick={copyLink}>
                  <i className={`fas ${linkCopied ? "fa-check" : "fa-copy"}`}></i>
                  {linkCopied ? " Copied!" : " Copy"}
                </button>
              </div>
              <p className="m-sidebar-hint">Share this link to invite others to this meeting.</p>

              <div className="m-section-label" style={{ marginTop: "20px" }}>Room code</div>
              <div className="m-room-code-display">
                <span>{roomId}</span>
                <button className="m-link-copy-btn" onClick={copyLink}>
                  <i className={`fas ${linkCopied ? "fa-check" : "fa-copy"}`}></i>
                </button>
              </div>

              <div className="m-section-label" style={{ marginTop: "20px" }}>Your ID</div>
              <div className="m-room-code-display">
                <span>{userId}</span>
                <button className="m-link-copy-btn" onClick={copyId}>
                  <i className={`fas ${idCopied ? "fa-check" : "fa-copy"}`}></i>
                </button>
              </div>
            </div>
          )}
        </aside>
      </div>

      {/* ── Bottom Control Bar ── */}
      <div className="m-controls">
        <div className="m-ctrl-left">
          <span className="m-room-label">
            <i className="fas fa-video"></i> {roomId}
          </span>
        </div>

        <div className="m-ctrl-center">
          <button
            className={`m-ctrl-btn ${isMuted ? "m-ctrl-off" : ""}`}
            onClick={toggleMute}
            title={isMuted ? "Unmute" : "Mute"}
          >
            <i className={`fas ${isMuted ? "fa-microphone-slash" : "fa-microphone"}`}></i>
          </button>

          <button
            className={`m-ctrl-btn ${!isCameraOn ? "m-ctrl-off" : ""}`}
            onClick={toggleCamera}
            title={isCameraOn ? "Turn off camera" : "Turn on camera"}
          >
            <i className={`fas ${isCameraOn ? "fa-video" : "fa-video-slash"}`}></i>
          </button>

          <button
            className="m-ctrl-btn"
            onClick={switchCamera}
            title="Switch camera"
          >
            <i className="fas fa-sync-alt"></i>
          </button>

          <button
            className={`m-ctrl-btn ${isScreenSharing ? "m-ctrl-screen-active" : ""}`}
            onClick={toggleScreenShare}
            title={isScreenSharing ? "Stop sharing" : "Share screen"}
          >
            <i className={`fas ${isScreenSharing ? "fa-stop-circle" : "fa-desktop"}`}></i>
          </button>

          {!videoStarted ? (
            <button className="m-ctrl-btn m-ctrl-start" onClick={() => startVideo()}>
              <i className="fas fa-play"></i>
              <span>Start</span>
            </button>
          ) : (
            <button className="m-ctrl-btn m-ctrl-end" onClick={disconnectAll}>
              <i className="fas fa-phone-slash"></i>
              <span>End</span>
            </button>
          )}
        </div>

        <div className="m-ctrl-right">
          <button className="m-ctrl-btn m-ctrl-sm" onClick={() => setSidebarTab("people")}>
            <i className="fas fa-users"></i>
            <span>{1 + peerEntries.length}</span>
          </button>
          <button className="m-ctrl-btn m-ctrl-sm" onClick={() => setSidebarTab("info")}>
            <i className="fas fa-info-circle"></i>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Meet;
